import React, { useState, useEffect, useRef } from 'react';
import {
    IonBadge, IonButton, IonIcon,
    IonModal, IonList, IonItem, IonLabel, IonHeader, IonToolbar, IonTitle, IonContent,
    IonText,
    IonRow,
    IonCol,
    useIonLoading,
    useIonToast,
    IonNote,
    IonInput,
    useIonModal,
    IonPage,
    IonCard,
    IonCardHeader,
    IonSegment,
    IonSegmentButton,
    IonCardSubtitle,
    IonCardContent,
    IonSelect,
    IonSelectOption,
    IonItemDivider,
    IonButtons
} from '@ionic/react';
import { notificationsOutline, close, closeOutline, chatbubblesOutline, checkmarkSharp, alertSharp, checkmark, copySharp, car } from 'ionicons/icons';

import { Clipboard } from '@capacitor/clipboard';

import { format } from 'date-fns';

import axiosCall from '../data/axios';

import { getCaptchaToken } from "../Helper";

import AuthContext from "../UserContext";
import { useHistory } from 'react-router';
import { OverlayEventDetail } from '@ionic/react/dist/types/components/react-component-lib/interfaces';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

const formatTimestamp = (timestamp: any) => {
    return format(new Date(timestamp), 'PPpp'); // PPpp gives a readable format like June 6, 2024 at 11:37 AM
};

interface RMAPaymentGatewayProps {
    total: number;
    payFunction: (event: any) => Promise<void>;
    purchaserData: {
        isExistingUser: boolean;
        isUserFormValid: boolean;
        userData: {};
    };
    onSuccess: any;     // Assuming api is optional
}

export const RMAPaymentGateway : React.FC<RMAPaymentGatewayProps> = ({ 
    total,
    payFunction,
    purchaserData,
    onSuccess
}) => {

    const [error, setError] = useState('');
    const [rmaInitMsg, setRmaInitMsg] = useState('Communicating with');

    const OtpModal = ({ dismiss }: { dismiss: (data?: any | null | undefined | number, role?: string) => void }) => {
        const inputRef = useRef<HTMLIonInputElement>(null);
        let otpConfirm = function() {
            if (inputRef.current?.value) {
                dismiss({ otpCode: inputRef.current?.value }, 'confirm');
            }else {
                inputRef.current?.setFocus();
            }
        }
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonTitle> Authorise Payment</IonTitle>
                    </IonToolbar>
                </IonHeader>
                <IonContent className="ion-padding">
                    <IonItem className="ion-padding">
                        Please enter the OTP code that would have been sent to your registered mobile number.
                    </IonItem>
                    <IonRow className="ion-padding">
                        <IonCol size="8" offset='2' className="ion-text-center">
                            <IonInput ref={inputRef} labelPlacement="floating" 
                                fill="outline" label="Enter the code here" type="number" />
                        </IonCol>
                    </IonRow>
                    <IonItemDivider/>
                    <IonItem>
                        <IonToolbar>
                            <IonButtons slot="start">
                                <IonButton color="medium" onClick={() => dismiss(null, 'cancel')}>
                                    Cancel
                                </IonButton>
                            </IonButtons>
                            <IonButtons slot="end">
                                <IonButton color="drukre" onClick={() => otpConfirm()} strong={true}>
                                    Submit
                                </IonButton>
                            </IonButtons>
                        </IonToolbar>
                    </IonItem>
                </IonContent>
            </IonPage>
        );
    };

    const [bfsTxnId, setBfsTxnId] = useState('');
    const [bfsBankList, setBfsBankList] = useState([]);

    const [rmaBank, setRmaBank] = useState('');
    const [rmaAccountNo, setRmaAccountNo] = useState('');

    const [presentOTP, setPresentOTP] = useIonModal(OtpModal, {
        dismiss: (data: string, role: string) => setPresentOTP(data, role)
    });
    
    const [spinnerPresent, spinnerDismiss] = useIonLoading();

    const processOTP = async () => {

        setError('');

        if (!rmaBank || !rmaAccountNo) {
            setError("Please select a bank and enter account number.");
            return;
        }

        var captchaData = {
            captchaToken: '',
            captchaAction: ''
        };

        await getCaptchaToken('PAYMENT').then((token: any) => { // Here wait token generated
            if (token) {
                captchaData.captchaToken = token;
                captchaData.captchaAction = 'PAYMENT';
            }
        });

        spinnerPresent({
            message: 'Processing. Please wait...',
            spinner: 'bubbles',
            animated: true,
            cssClass: 'drukre-loading'
        });

        //Fire backend rma api call
        axiosCall("/api/rma-payment-ae", 'post', 
            { bank: rmaBank, accountNo: rmaAccountNo, transactionId: bfsTxnId, captchaData: captchaData }, true)
        .then((res) => {
            if (res.data.status == '00'){
                presentOTP({
                    initialBreakpoint: 0.5,
                    breakpoints: [0.35, 0.5, 0.7, 1],
                    backdropBreakpoint: 0,
                    backdropDismiss: false,
                    onWillDismiss: (ev: CustomEvent<OverlayEventDetail>) => {
                        if (ev.detail.role === 'confirm') {
                            (ev.detail as any).rmaBank = rmaBank;
                            (ev.detail as any).rmaAccountNo = rmaAccountNo;
                            (ev.detail as any).bfs_bfsTxnId = bfsTxnId;
                            afterOTP(ev);
                        }
                    }
                });
            } else {
                setError('Payment process unsuccessful! ' + res.data.data);
                setRmaInitMsg('Reinitiating');
            }
        }).catch((error) => {
            setError("Payment initiation unsuccessful! Please try again.");
            setRmaInitMsg('Reinitiating');
            //setIserror(true);
        }).finally(() => {
            spinnerDismiss();
        });
    };

    function afterOTP(ev: any) {

        setPresentOTP();

        payFunction(ev);
    }

    useEffect(() => {
        
        if(total < 1) return;

        let isMounted = true;
        
        const rmaIntent = async () => {
            try {
                var captchaData = {
                    captchaToken: '',
                    captchaAction: ''
                };

                await getCaptchaToken('PAYMENT').then((token:any) => {
                    if (token) {
                        captchaData.captchaToken = token;
                        captchaData.captchaAction = 'PAYMENT';
                    }
                });

                if (isMounted) {
                    spinnerPresent({
                        message: rmaInitMsg + ' Payment Gateway. Please wait...',
                        spinner: 'bubbles',
                        animated: true,
                        cssClass: 'drukre-loading'
                    });

                    axiosCall("/api/rma-payment-intent", 'post', 
                        { captchaData: captchaData, amount: total }, true)
                    .then((res) => {
                        if (isMounted) {
                            setBfsTxnId(res.data.data.bfs_bfsTxnId);
                            setBfsBankList(res.data.data.bfs_bankList);
                        }
                    })
                    .catch((error) => {
                        if (isMounted) {
                            setRmaInitMsg('Reinitiating');
                        }
                    })
                    .finally(() => {
                        if (isMounted) {
                            spinnerDismiss();
                        }
                    });
                }
            } catch (error) {
                console.error('Error fetching captcha token:', error);
            }
        };

        rmaIntent();

        return () => {
            isMounted = false;
        };
    }, [rmaInitMsg, total]);

    return (
        <IonCard>
            <IonCardHeader>
                <IonCardSubtitle className="ion-text-center"> </IonCardSubtitle>
            </IonCardHeader>
            <IonCardContent>
                <IonLabel position="stacked">Your order will be placed through RMA's Payment Gateway.</IonLabel>
                <IonRow>
                    <IonCol>
                        <IonSelect placeholder="Select Bank" onIonChange={(e) => setRmaBank(String(e.detail.value))}>
                            {bfsBankList &&
                            Object.entries(bfsBankList).map(([key, value]) => (
                                <IonSelectOption key={key} value={key}>{value}</IonSelectOption>
                            ))}
                        </IonSelect>
                    </IonCol>
                </IonRow>
                <IonRow>
                    <IonCol>
                        <IonInput type="number" labelPlacement="floating" fill="outline" label="Account Number" 
                            placeholder="Enter Account Number" 
                            onIonInput={(e) => setRmaAccountNo(String(e.detail.value))} required />
                    </IonCol>
                </IonRow>
                {error && <p className="ion-text-center"><IonLabel color="danger">{error}</IonLabel></p>}
                <IonButton className="ion-padding ion-float-right" color="drukre" onClick={processOTP} disabled={!purchaserData.isUserFormValid}>
                    <IonIcon icon={checkmarkSharp} />&nbsp; Pay With RMA
                </IonButton>
            </IonCardContent>
        </IonCard>
    );
};

// Exchange rate function that can be used in other components as well
export const getExchangeRate = async (from: string, to: string): Promise<number | null> => {
    try {
        const response = await fetch(`https://api.exchangerate-api.com/v4/latest/${from}`);
        const data = await response.json();
        return data.rates[to] || null; // fallback to null if API fails
    } catch (error) {
        console.error('Exchange rate fetch failed:', error);
        return null; 
    }
};

//PAYPAL COMPONENT
const PayPalPayment = ({ total, onSuccess, onError, payFunction, purchaserData }: {
    total: number;
    onSuccess: any;
    onError: (error: string) => void;
    payFunction: (details: any) => void;
    purchaserData: any;
}) => {
    const [spinnerPresent, spinnerDismiss] = useIonLoading();

    // Detect dark mode
    const isDarkMode = document.body.classList.contains('dark') || 
                      window.matchMedia('(prefers-color-scheme: dark)').matches;

    return (
        <PayPalScriptProvider options={{
            clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID || "your-paypal-client-id",
            currency: "AUD", // Keep AUD for better card support
            intent: "capture",
            "enable-funding": "card,venmo", // Enable card payments
            "disable-funding": "paylater", // Only disable pay later
            vault: false,
            locale: "en_AU",
            // Add these container styling options
            "data-theme": isDarkMode ? "dark" : "light", // PayPal's theme support
            "data-color-scheme": isDarkMode ? "dark" : "light",
            components: "buttons",
        }}>
            <div 
                className="paypal-container"
                style={{
                    '--paypal-color-background': 'var(--ion-card-background)',
                    '--paypal-color-text': 'var(--ion-text-color)',
                    '--paypal-border-radius': '8px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    padding: '10px'
                } as React.CSSProperties}
            >
            <PayPalButtons
                style={{
                    layout: "vertical",
                    color: "blue",
                    shape: "rect",
                    label: "pay",
                }}
                createOrder={async (data, actions) => {
                    try {
                        var captchaData = {
                            captchaToken: '',
                            captchaAction: ''
                        };

                        await getCaptchaToken('PAYMENT').then((token:any) => {
                            if (token) {
                                captchaData.captchaToken = token;
                                captchaData.captchaAction = 'PAYMENT';
                            }
                        });
                        
                        spinnerPresent({
                            message: 'Initiating Payment. Please wait...',
                            spinner: 'bubbles',
                            animated: true,
                            cssClass: 'drukre-loading'
                        });

                        let ppOrderID = '00';

                        // Convert Nu to AUD
                        let exchangeRate = await getExchangeRate('AUD', 'BTN') || 53; // Fallback rate if API fails
                        const audAmount = (total / exchangeRate).toFixed(2);

                        await axiosCall("/api/create-paypal-order", 'post', 
                            { captchaData: captchaData, amount: parseFloat(audAmount), purchaserData: purchaserData }, true)
                        .then((res) => {
                            console.log('PayPal order created:', res.data);
                            ppOrderID = res.data.id;
                        })
                        .catch((error) => {
                            console.error('Create order failed:', error);
                            throw error;
                        })
                        .finally(() => {
                            spinnerDismiss();
                        });
                        return ppOrderID;
                    } catch (error) {
                        console.error('Error creating PayPal order:', error);
                        spinnerDismiss();
                        throw error;
                    }
                }}
                onApprove={async (data, actions) => {
                    try {
                        console.log('Payment approved:', data);
                        
                        spinnerPresent({
                            message: 'Processing payment...',
                            spinner: 'bubbles',
                            animated: true,
                            cssClass: 'drukre-loading'
                        });

                        // Send PayPal transaction details to your backend
                        var captchaData = {
                            captchaToken: '',
                            captchaAction: ''
                        };

                        await getCaptchaToken('PAYMENT').then((token: any) => {
                            if (token) {
                                captchaData.captchaToken = token;
                                captchaData.captchaAction = 'PAYMENT';
                            }
                        });

                        let ppData = {
                            paypalOrderId: data.orderID,
                            payerId: data.payerID,
                            captchaData: captchaData
                        };
                        
                        // Create mock event for your existing handler
                        const mockEvent = {
                            preventDefault: () => {},
                            detail: ppData
                        };
                        
                        await payFunction(mockEvent);
                        spinnerDismiss();

                    } catch (error) {
                        spinnerDismiss();
                        console.error('Payment approval error:', error);
                        onError('Payment failed: ' + error);
                    }
                }}
                onError={(err: any) => {
                    spinnerDismiss();
                    console.error('PayPal Error Details:', err);
                    
                    // Handle specific errors
                    if (err.message && err.message.includes('CURRENCY_CONVERSION_ERROR')) {
                        onError('Currency conversion error. Please try using a USD card or PayPal account.');
                    } else if (err.message && err.message.includes('PAYMENT_DENIED')) {
                        onError('Payment was denied. Please check your card details or try a different card.');
                    } else {
                        onError('Payment failed. Please try again or use a different payment method.');
                    }
                }}
                onCancel={() => {
                    spinnerDismiss();
                    console.log('Payment cancelled');
                }}
            />
            </div>
        </PayPalScriptProvider>
    );
};
//PAYPAL COMPONENT END

const PaymentForm = ({ purchaserData, payLoad, api, total, onSuccess }: {purchaserData: any, 
    payLoad:any, api: any, total: number, onSuccess: any}) => {
    const { authValues, fetchUser, logout } = React.useContext(AuthContext);
    const [name, setName] = useState('');
    const [cardNumber, setCardNumber] = useState('');
    const [exp, setExp] = useState('');
    const [cvv, setCvv] = useState('');
    const [error, setError] = useState('');
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const [giftCardNumber, setGiftCardNumber] = useState('');
    const [giftCardCode, setGiftCardCode] = useState('');
    const [giftCards, setGiftCards] = useState<Array<{ cardNumber: string; code: string }>>([]);

    const [spinnerPresent, spinnerDismiss] = useIonLoading();
    const [presentToast] = useIonToast();
    const history = useHistory();

    const [ paymentMode, setPaymentMode ] = useState<string>('paypal');
    const [ walletBalance, setWalletBalance ] = useState(0);

    const [stagedAmount, setStagedAmount] = useState(0);
    const [stagedRef, setStagedRef] = useState('');

    const [pushRegToken, setPushRegToken] = useState('');
        
    useEffect(() => {
        const registerPush = async () => {
            if (Capacitor.isNativePlatform()) {
                const pushTokenData = {
                    "captchaToken": '',
                    "captchaAction": '',
                    "device_fcm_token": pushRegToken
                }
                await getCaptchaToken('SIGNUP').then((token) => { // Here wait token generated
                    if (token) {
                        pushTokenData.captchaToken = String(token) || '';
                        pushTokenData.captchaAction = 'SIGNUP';
                    }
                });
                await PushNotifications.addListener('registration', token => {
                    console.log('Push registration success, token: ' + token.value);
                    setPushRegToken(token.value);
                    //Assign FCM token to purchaserData
                    Object.assign(purchaserData, { device_fcm_token: token.value });
                    
                    pushTokenData.device_fcm_token = token.value;
                    axiosCall("/api/saveDeviceToken", 'post', pushTokenData, true)
                        .then((res) => {
                            console.log("Device token saved");
                        })
                        .catch((error) => {
                            console.error("Device token save failed: " + error.message);
                        });
                });

                await PushNotifications.addListener('registrationError', error => {
                    console.error('Push registration error: ', error);
                    throw error;
                });

                let permStatus = await PushNotifications.checkPermissions();

                if (permStatus.receive === 'prompt') {
                    permStatus = await PushNotifications.requestPermissions();
                }

                if (permStatus.receive !== 'granted') {
                    throw new Error('User denied permissions!');
                }

                await PushNotifications.register();
            }
        };

        registerPush();
    }, []);

    useEffect(() => {
        axiosCall("/api/wallet", 'get', null, true).then((res) => {
            setWalletBalance(res.data.balance);
        })
        .catch((error) => {
            //setError("Couldn't fetch wallet balance.");
            //setIserror(true);
        });
    }, []);

    const addGiftCard = () => {
        const normalizedCardNumber = (giftCardNumber || '').replace(/\s+/g, '');
        const normalizedCode = (giftCardCode || '').trim();

        if (!normalizedCardNumber || !normalizedCode || normalizedCardNumber.length < 16) {
            setError('Please enter a valid giftcard number and code.');
            return;
        }

        const alreadyAdded = giftCards.some((giftCard) =>
            giftCard.cardNumber === normalizedCardNumber && giftCard.code === normalizedCode
        );

        if (alreadyAdded) {
            setError('This giftcard has already been added.');
            return;
        }

        setGiftCards((prevGiftCards) => [...prevGiftCards, { cardNumber: normalizedCardNumber, code: normalizedCode }]);
        setGiftCardNumber('');
        setGiftCardCode('');
        setError('');
    };

    const removeGiftCard = (index: number) => {
        setGiftCards((prevGiftCards) => prevGiftCards.filter((_, giftCardIndex) => giftCardIndex !== index));
    };

    const handlePayment = async (event: any) => {

        event.preventDefault();

        const cardObject = {
            name: name,
            cardNumber: cardNumber,
            exp: exp,
            cvv: cvv,
            code: ''
        };

        let selectedGiftCards = [...giftCards];

        setError('');

        if (paymentMode == 'card' && (Number(cardNumber) < 444411111111 || Number(cvv) < 975)) {
            setError("Invalid Card Details");
            return;
        }

        if (paymentMode == 'giftcard') {
            const normalizedCardNumber = (giftCardNumber || '').replace(/\s+/g, '');
            const normalizedCode = (giftCardCode || '').trim();

            // Include the in-progress entry at submit time so users can redeem without clicking Add.
            if (normalizedCardNumber || normalizedCode) {
                if (!normalizedCode || normalizedCardNumber.length < 16) {
                    setError('Please enter a valid giftcard number and code.');
                    return;
                }

                const pendingCardExists = selectedGiftCards.some((giftCard) =>
                    giftCard.cardNumber === normalizedCardNumber && giftCard.code === normalizedCode
                );

                if (!pendingCardExists) {
                    selectedGiftCards.push({ cardNumber: normalizedCardNumber, code: normalizedCode });
                }
            }

            if (!selectedGiftCards.length) {
                setError('Please add at least one valid giftcard.');
                return;
            }

            cardObject.cardNumber = selectedGiftCards[0].cardNumber;
            cardObject.code = selectedGiftCards[0].code;
        }

        if(paymentMode == 'wallet' && total > walletBalance)
        {
            setError("Insufficient wallet balance. Please add funds first.");
            return;
        }

        const otpData = {
            code: '',
            rmaAccountNo: '',
            rmaBank: '',
            bfsTransactionId: ''
        };

        if(paymentMode == 'rma') {
            otpData.code = event.detail.data.otpCode;
            otpData.rmaAccountNo = event.detail.rmaAccountNo;
            otpData.rmaBank = event.detail.rmaBank;
            otpData.bfsTransactionId = event.detail.bfs_bfsTxnId;
        }

        // Send paymentMethod.id to your server to create a payment intent
        // For example:
        // const response = await fetch('/create-payment-intent', {
        //   method: 'POST',
        //   headers: { 'Content-Type': 'application/json' },
        //   body: JSON.stringify({ payment_method: paymentMethod.id }),
        // });
        // const paymentIntent = await response.json();

        const headers = {
            'Content-Type': 'application/json'
        }

        var captchaData = {
            captchaToken: '',
            captchaAction: ''
        };

        await getCaptchaToken('PAYMENT').then((token: any) => { // Here wait token generated
            if (token) {
                captchaData.captchaToken = token;
                captchaData.captchaAction = 'PAYMENT';
            }
        });

        spinnerPresent({
			message: 'Processing. Please wait...',
			spinner: 'bubbles',
			animated: true,
			cssClass: 'drukre-loading'
		});

        axiosCall(api, 'post', 
            { 
                cardObject: cardObject,
                giftCards: selectedGiftCards,
                paymentMethod: paymentMode,
                otpData: otpData,
                payLoad: payLoad,
                paymentData: event.detail,
                purchaser_details: purchaserData, 
                captchaData: captchaData 
            }, 
            true
        ).then((res) => {
            console.log(res);
            if (res.data.status !== 1){
                setError('Payment unsuccessful! ' + res.data.message);
            }
            else {
                setPaymentSuccess(true);
                onSuccess(res.data.message);
                presentToast({
                    message: 'Payment Successful! ' + res.data.message + '. Please track your order using the order number.',
                    color: 'success',
                    buttons: [
                        {
                            text: 'Dismiss',
                            role: 'cancel'
                        }
                    ]
                });
            }
        })
        .catch((error) => {
            setError("Unsuccessful! " + error);
            //setIserror(true);
        }).finally(() => {
            spinnerDismiss();
        });
    };

    const addFundToWallet = async () => {
            
        if (stagedAmount < 1) {
            setError("Please enter a valid amount to add.");
            return;
        }

        if (!stagedRef) {
            setError("Please enter a reference or description identifiable for confirmation.");
            return;
        }

        const headers = {
            'Content-Type': 'application/json'
        }

        var captchaData = {
            captchaToken: '',
            captchaAction: ''
        };

        await getCaptchaToken('PAYMENT').then((token:any) => { // Here wait token generated
            if (token) {
                captchaData.captchaToken = token;
                captchaData.captchaAction = 'PAYMENT';
            }
        });

        spinnerPresent({
            message: 'Processing. Please wait...',
            spinner: 'bubbles',
            animated: true,
            cssClass: 'drukre-loading'
        });

        const otpData = {
            code: '',
            rmaAccountNo: '',
            rmaBank: '',
            bfsTransactionId: ''
        };

        setError('');
        
        axiosCall("/api/wallet", 'post',
            {
                paymentMethod: 'eft',
                otpData: otpData,
                amount: stagedAmount,
                description: stagedRef,
                type: 'credit',
                captchaData: captchaData
            }, true
        ).then((res) => {
            console.log(res);
            if (res.data.status !== 1){
                setError('Fund submission unsuccessful! ' + res.data.message);
            }
            else {
                //setWalletBalance(walletBalance + stagedAmount);
                presentToast({
                    message: 'Fund submission successfully! You will be notified when it becomes available in your wallet.',
                    icon: checkmark,
                    color: 'success',
                    buttons: [
                        {
                            text: 'Dismiss',
                            role: 'cancel'
                        }
                    ]
                });
            }
        })
        .catch((error) => {
            setError("Fund submission unsuccessful! Please try again.");
            //setIserror(true);
        }).finally(() => {
            spinnerDismiss();
        });
    };

    const copyToClipboard = async (text: string): Promise<void> => {
        try {
            await Clipboard.write({
                string: text
            });
            presentToast({
                message: 'Account number copied to clipboard.',
                duration: 3000,
                color: 'success'
            });
        } catch (err) {
            console.error('Failed to copy text:', err);
            presentToast({
                message: 'Failed to copy text to clipboard.',
                duration: 3000,
                color: 'danger'
            });
        }
    };

    return (
        <form onSubmit={handlePayment}> 
            <IonCard>
                <IonCardHeader>
                    <IonToolbar>
                        <IonSegment onIonChange={e => setPaymentMode(String(e.detail.value) || 'paypal')}>
                            <IonSegmentButton value="paypal">
                                <IonLabel>PayPal</IonLabel>
                            </IonSegmentButton>
                            <IonSegmentButton value="rma">
                                <IonLabel>RMA Payment</IonLabel>
                            </IonSegmentButton>
                            <IonSegmentButton value="wallet">
                                <IonLabel>Wallet Payment</IonLabel>
                            </IonSegmentButton>
                            <IonSegmentButton value="giftcard">
                                <IonLabel>Giftcard Payment</IonLabel>
                            </IonSegmentButton>
                            {/*
                            <IonSegmentButton value="card">
                                <IonLabel>Card Payment</IonLabel>
                            </IonSegmentButton>
                            */}
                        </IonSegment>
                    </IonToolbar>
                    <IonCardSubtitle className="ion-text-center">
                        Total Amount: <span style={{ fontWeight: 'bold', color: '#cb1a27' }}>Nu.{total.toFixed(2)} </span> (GST Inclusive)
                    </IonCardSubtitle>
                </IonCardHeader>
                {error && <p className="ion-text-center"><IonLabel color="danger">{error}</IonLabel></p>}
                {paymentMode == 'card' ?
                    <IonCardContent>
                        <IonItem>
                            <IonLabel position="stacked">Your Name</IonLabel>
                            <IonInput value={name} onIonInput={(e) => setName(String(e.detail.value))} required />
                        </IonItem>
                        <IonItem>
                            <IonLabel position="stacked">Card Number</IonLabel>
                            <IonInput type="number" value={cardNumber} onIonInput={(e) => setCardNumber(String(e.detail.value))} required />
                        </IonItem>
                        <IonRow>
                            <IonCol>
                                <IonItem>
                                    <IonLabel position="stacked">Expiry</IonLabel>
                                    <IonInput type="month" min="2025-01" value={exp} onIonInput={(e) => setExp(String(e.detail.value))} required />
                                </IonItem>
                            </IonCol>
                            <IonCol>
                                <IonItem>
                                    <IonLabel position="stacked">CVV</IonLabel>
                                    <IonInput max={1000} maxlength={3} type="number" value={cvv} onIonInput={(e) => setCvv(String(e.detail.value))} required />
                                </IonItem>
                            </IonCol>
                        </IonRow>
                        <p style={{ fontSize: "small" }} className="ion-padding">
                            All card payments are routed through <a href="https://stripe.com" target='_blank'>our Third Party Payment Gatewaya</a>. 
                            By Proceeding you agree to our <a href="/terms-and-conditions">Terms & Conditions</a>
                        </p>
                        {paymentSuccess ? (
                            <p>Payment Successful!</p>
                        ) : (
                            <IonButton className="ion-padding ion-float-right" color="drukre" type="submit" disabled={!purchaserData.isUserFormValid}>
                                <IonIcon icon={checkmarkSharp} />&nbsp; Pay Now
                            </IonButton>
                        )}
                    </IonCardContent>
                : paymentMode == 'giftcard' ?
                    <IonCardContent>
                        <IonRow>
                            <IonCol size="12" className="ion-align-items-center">
                                Please enter one or more EThrom giftcards and the associated codes.
                            </IonCol>
                        </IonRow>
                        <IonRow className="ion-justify-content-start ion-align-items-center">
						    <IonCol size="12">
                                <IonInput type="text" labelPlacement="floating" fill="outline" label="Gift Card Number" 
                                placeholder="Enter Giftcard Number" value={giftCardNumber}
                                onIonInput={(e) => setGiftCardNumber(String(e.detail.value))} />
                            </IonCol>
                        </IonRow>
                        <IonRow className="ion-justify-content-start ion-align-items-center">
                            <IonCol size="12">
                                <IonInput type="text" labelPlacement="floating" fill='outline' label="Gift Card Code" 
                                    placeholder='Enter Giftcard Code' value={giftCardCode}
                                    onIonInput={(e) => setGiftCardCode(String(e.detail.value))} />
                            </IonCol>
                        </IonRow>
                        <IonRow>
                            <IonCol size="12" className="ion-text-right">
                                <IonButton color="secondary" type="button" onClick={addGiftCard}>
                                    Add Giftcard
                                </IonButton>
                            </IonCol>
                        </IonRow>
                        {giftCards.length > 0 && (
                            <IonList>
                                {giftCards.map((giftCard, index) => (
                                    <IonItem key={`${giftCard.cardNumber}-${index}`} style={{ '--background': 'var(--ion-color-light)', borderRadius: '8px', marginBottom: '10px' }}>
                                        <IonLabel>
                                            <h3>{giftCard.cardNumber}</h3>
                                            <p>Code: {giftCard.code}</p>
                                        </IonLabel>
                                        <IonButton slot="end" fill="clear" color="danger" type="button" onClick={() => removeGiftCard(index)}>
                                            <IonIcon icon={closeOutline} />
                                        </IonButton>
                                    </IonItem>
                                ))}
                            </IonList>
                        )}
                            <IonButton className="ion-padding ion-float-right" color="drukre" type="submit" disabled={!purchaserData.isUserFormValid || giftCards.length === 0}>
                                <IonIcon icon={checkmarkSharp} />&nbsp; Redeem Now & Pay
                            </IonButton>
                    </IonCardContent>
                : paymentMode == 'rma' ?
                    <RMAPaymentGateway total={total} payFunction={handlePayment} purchaserData={purchaserData} onSuccess={onSuccess} />
                : paymentMode == 'paypal' ?
                    <IonCardContent>
                        <IonRow className="ion-justify-content-start ion-align-items-center">
                            <IonCol size="12" className="ion-align-items-center ion-text-center ion-padding">
                                <IonLabel position="stacked">Pay with Card or PayPal</IonLabel>
                                <IonText color="medium" style={{ fontSize: '0.9em', marginBottom: '10px', display: 'block' }}>
                                    You can pay with your credit/debit card or PayPal account. 
                                    Amount will be converted from Nu. {total.toFixed(2)} to AUD at current exchange rate at the time of payment.
                                </IonText>
                                <div className="ion-text-center" style={{ maxWidth: '500px', margin: '0 auto' }}>
                                    <PayPalPayment
                                        total={total} 
                                        onSuccess={onSuccess}
                                        onError={setError}
                                        payFunction={handlePayment}
                                        purchaserData={purchaserData}
                                    />
                                </div>
                            </IonCol>
                        </IonRow>
                    </IonCardContent>
                : authValues.authenticated ?
                    <IonCardContent>
                        <IonItem>
                            <IonLabel position="stacked">Your Wallet Balance : Nu. {walletBalance}</IonLabel>
                        </IonItem>
                        { walletBalance > total ?
                            <>
                                <IonItem>
                                    <IonLabel> Your order will be placed by fund deducted from your wallet. </IonLabel>
                                </IonItem>
                                <IonItem>
                                    <IonButton className="ion-padding ion-float-right" color="drukre" type="submit" disabled={!purchaserData.isUserFormValid}>
                                        <IonIcon icon={checkmarkSharp} />&nbsp; Pay With Wallet
                                    </IonButton>
                                </IonItem>
                            </>
                        :
                            <>
                                <IonItem>
                                    <IonLabel color="danger">You do not have enough fund. Add fund to your wallet first.</IonLabel>
                                </IonItem>
                                <IonRow>
                                    <IonCol>
                                        <IonItem>
                                            <IonLabel>Bank Name</IonLabel>
                                            <IonText>Bank of Bhutan</IonText>
                                        </IonItem>
                                        <IonItem>
                                            <IonLabel>Account Name</IonLabel>
                                            <IonText>EThrom</IonText>
                                        </IonItem>
                                        <IonItem>
                                            <IonLabel>Account Number</IonLabel>
                                            <IonText>219127850</IonText> &nbsp; &nbsp;
                                            <IonButton onClick={() => copyToClipboard('219127850')}>
                                                <IonIcon icon={copySharp} />&nbsp;
                                                Copy</IonButton>
                                        </IonItem>
                                    </IonCol>
                                </IonRow>
                                <IonRow>
                                    <IonCol>
                                        <IonLabel position="stacked"> 
                                            Please deposit amount into the above account using your bank app or by other means and submit the details below.
                                        </IonLabel>
                                    </IonCol>
                                </IonRow>
                                <IonRow className='ion-padding'>
                                    <IonInput type="number" label="Amount" 
                                        onIonInput={(e) => setStagedAmount(Number(e.detail.value))} 
                                        labelPlacement="floating" fill="outline" required />
                                </IonRow>
                                <IonRow className='ion-padding'>
                                    <IonInput type="text" label="Reference #/Description"
                                        onIonInput={(e) => setStagedRef(String(e.detail.value))} 
                                        labelPlacement="floating" fill="outline" required />
                                        <IonNote>Enter bank reference or description identifiable for confirmation.</IonNote>
                                </IonRow>
                                <IonItem>
                                    <IonButton className="ion-padding ion-float-right" onClick={addFundToWallet} color="drukre" 
                                        disabled={!authValues.authenticated}>
                                        <IonIcon icon={checkmarkSharp} />&nbsp; Submit Now
                                    </IonButton>
                                </IonItem>
                             </>
                        }
                    </IonCardContent> 
                    : 
                    <IonCardContent>
                        <IonCardSubtitle className="ion-text-center">
                            <IonLabel position="stacked">Please login first to use Wallet or use another payment method.</IonLabel>
                        </IonCardSubtitle>
                    </IonCardContent>
                }
            </IonCard>
        </form>
    );
};

export default PaymentForm;
